import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IModalProps } from "config/init/providers/ModalsProvider";
import { Layout, useTranslation } from "modules";
import { RootState, useAppDispatch } from "store";
import { restoreJourney } from "store/actions/journey";
import { closeModal } from "store/actions/modal";
import { requestPolicyNewBusiness, requestPolicyReinstatement } from "store/actions/policy";
import { getJourneyExitPath } from "utils/journey";

import { find, first, isEmpty, isEqual } from "lodash-es";
import store2 from "store2";

import type { JourneyState } from "store/reducers/journey";

const { local } = store2;

const JourneyProgress: FC<IModalProps> = () => {
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state: RootState) => state.user.id);
  const storage = isEmpty(userId) ? local : local.namespace(userId as string);

  const handleResumeJourney = async () => {
    const journeyState = storage.get("journeyProgress") as Partial<JourneyState> | undefined;
    if (!isEmpty(journeyState)) {
      const journeyProgressActive = first<string>(journeyState?.activeJourneys || []) || "";
      const isJourneyProgressReinstatement = isEqual(
        journeyProgressActive,
        "reinstatementFulfillment"
      );
      const isJourneyProgressFulfillment = isEqual(journeyProgressActive, "fulfillment");
      if (isJourneyProgressReinstatement) {
        const metadata = journeyState?.collection?.[journeyProgressActive]?.metadata as
          Record<string, unknown> | undefined;
        const policyIdValue = metadata?.["reinstateablePolicyId"];
        const policyId = typeof policyIdValue === "string" ? policyIdValue : undefined;
        if (policyId) {
          await appDispatch(requestPolicyReinstatement(policyId)).unwrap();
        }
      } else if (isJourneyProgressFulfillment) {
        await appDispatch(requestPolicyNewBusiness({ product: "wysh-term-life" })).unwrap();
      }
      dispatch(restoreJourney(journeyState));
      closeModalWrapper(false);
    }
  };

  const closeModalWrapper = (navigateToExitPath = true) => {
    const journeyState = storage.get("journeyProgress");
    const activeJourney = find(journeyState.collection, { active: true });
    const exitPath = getJourneyExitPath(activeJourney);

    storage.remove("journeyProgress");
    dispatch(closeModal());

    if (navigateToExitPath && exitPath) {
      navigate(exitPath, { replace: true, state: { ignoreAction: true } });
    }
  };

  const ctaButtons = [
    {
      text: t("journey.journey_resume.modal.button_confirm", "Resume Journey"),
      onClick: handleResumeJourney,
    },
    {
      text: t("journey.journey_resume.modal.button_cancel", "Cancel"),
      subType: "text-dark",
      onClick: () => closeModalWrapper(),
    },
  ];

  const content = {
    header: t("journey.journey_resume.modal.title", "Journey In Progress"),
    subheader: t(
      "journey.journey_resume.modal.body",
      "You're in the middle of a journey. Don't abandon it! Get in there!"
    ),
    onClose: () => closeModalWrapper(),
    button: ctaButtons,
    icon: "BlobAlert",
  };

  return <Layout as="ModalLayout" content={content} />;
};

export default JourneyProgress;
